import { Component, signal, inject, OnInit, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router, ActivatedRoute } from '@angular/router';
import { AdminUsersComponent } from './admin-users/admin-users.component';
import { UserRolesComponent } from './user-roles/user-roles.component';
import { BreadcrumbService } from '../../core/services/breadcrumb.service';
import { ActionFeedbackService } from '../../core/services/action-feedback.service';
import { SearchInputComponent } from '../../shared/components/search-input/search-input.component';
import { PermissionService } from '../../core/services/permission.service';
import { SYSTEM_PERMISSION_LIST } from '../../shared/constants/permissions.constants';
import { FilterModalComponent } from '../../shared/components/filter-modal/filter-modal.component';
import { FilterModalConfig, FilterOption } from '../../core/models/filter-modal.model';
import { AioButtonComponent, AioSegmentedControlComponent, AioHeaderComponent } from '@aio/ui';
import {
  AdminUserListMode,
  ADMIN_USER_LIST_PAGE_TITLES,
  ADMIN_USER_LIST_VIEW_MODE_UI_OPTIONS,
  ADMIN_USER_TYPE_QUERY_PARAM,
  adminUsersListNavigationExtras,
  normalizeAdminUserListModeForUi,
  parseAdminUserListModeFromQuery,
} from './admin-user-list-mode.model';

@Component({
  selector: 'app-admin',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    AdminUsersComponent,
    UserRolesComponent,
    SearchInputComponent,
    AioButtonComponent,
    AioHeaderComponent,
    FilterModalComponent,
    AioSegmentedControlComponent,
  ],
  templateUrl: './admin.component.html',
  styleUrls: ['./admin.component.scss']
})
export class AdminComponent implements OnInit {
  selectedTab = signal<'users' | 'teams' | 'roles' | 'subscription'>('users');
  readonly usersViewMode = signal<AdminUserListMode>('internal');
  readonly usersViewModeOptions = ADMIN_USER_LIST_VIEW_MODE_UI_OPTIONS;

  @ViewChild(AdminUsersComponent) private adminUsersRef?: AdminUsersComponent;
  @ViewChild(UserRolesComponent) private userRolesRef?: UserRolesComponent;


  usersTotalCount = 0;
  rolesTotalCount = 0;
  usersHeaderSearchTerm = '';
  rolesHeaderSearchTerm = '';

  /** Placeholder until a dedicated KPI/counts API exists (avoid extra paginated calls). */
  usersKpiTotal = 86;
  usersKpiActive = 55;
  usersKpiInvited = 27;
  usersKpiRestricted = 1;

  shellFiltersRole = false;
  shellFiltersStatus = false;

  roleFilterModalOpen = false;
  statusFilterModalOpen = false;
  roleFilterModalConfig: FilterModalConfig | null = null;
  statusFilterModalConfig: FilterModalConfig | null = null;
  
  private router = inject(Router);
  private route = inject(ActivatedRoute);
  private readonly breadcrumbService = inject(BreadcrumbService);
  private readonly actionFeedback = inject(ActionFeedbackService);
  private readonly permissionService = inject(PermissionService);

  ngOnInit(): void {
    this.setAdminBreadcrumbs();
    this.checkForTabParameter();
    this.checkForActionResultParameter();
    this.syncUsersViewModeFromRoute();
    this.route.queryParamMap.subscribe(() => this.syncUsersViewModeFromRoute());
  }

  /** Restore users list toggle from `?userType=` (e.g. when returning from add/edit user). */
  private syncUsersViewModeFromRoute(): void {
    const pathOnly = this.router.url.split('?')[0];
    if (!/\/admin\/users$/.test(pathOnly)) {
      return;
    }
    const raw = this.route.snapshot.queryParamMap.get(ADMIN_USER_TYPE_QUERY_PARAM);
    const mode = parseAdminUserListModeFromQuery(raw);
    if (this.usersViewMode() === mode) {
      return;
    }
    this.usersViewMode.set(mode);
    queueMicrotask(() => this.adminUsersRef?.onListModeChanged());
  }

  getPageTitle(): string {
    const currentUrl = this.router.url;
    if (currentUrl.includes('/admin/users')) {
      return ADMIN_USER_LIST_PAGE_TITLES[this.usersViewMode()];
    }
    if (currentUrl.includes('/admin/roles')) {
      return 'Roles';
    }
    return 'Admin';
  }

  onUsersViewModeChange(value: string): void {
    const mode = normalizeAdminUserListModeForUi(value as AdminUserListMode);
    if (this.usersViewMode() === mode) {
      return;
    }
    this.usersViewMode.set(mode);
    this.usersHeaderSearchTerm = '';
    this.shellFiltersRole = false;
    this.shellFiltersStatus = false;
    this.adminUsersRef?.onListModeChanged();
    if (/\/admin\/users$/.test(this.router.url.split('?')[0])) {
      void this.router.navigate([], {
        relativeTo: this.route,
        queryParams: adminUsersListNavigationExtras(mode).queryParams,
        queryParamsHandling: 'merge',
        replaceUrl: true,
      });
    }
  }

  onUsersToolbarSearchChange(term: string): void {
    this.adminUsersRef?.applyToolbarSearch(term);
  }

  onUsersTotalChange(total: number): void {
    this.usersTotalCount = total;
  }

  onRolesTotalChange(total: number): void {
    this.rolesTotalCount = total;
  }

  onRolesToolbarSearchChange(term: string): void {
    this.userRolesRef?.applyToolbarSearch(term);
  }

  onAddRoleFromHeader(): void {
    this.userRolesRef?.onAddNewRole();
  }

  onUsersHeaderSearchReset(): void {
    this.usersHeaderSearchTerm = '';
  }

  onAddUserFromHeader(): void {
    this.adminUsersRef?.openAddUser();
  }

  hasUsersAddEditPermission(): boolean {
    return this.permissionService.hasAnyPermissionSync([SYSTEM_PERMISSION_LIST.USERS_CREATE_UPDATE_ALL]);
  }

  hasRolesAddEditPermission(): boolean {
    return this.permissionService.hasAnyPermissionSync([SYSTEM_PERMISSION_LIST.ROLES_ADD_EDIT]);
  }

  onTextFiltersShellStateChange(state: { role: boolean; status: boolean }): void {
    this.shellFiltersRole = state.role;
    this.shellFiltersStatus = state.status;
  }

  showUsersClearFilters(): boolean {
    return this.shellFiltersRole || this.shellFiltersStatus || !!this.usersHeaderSearchTerm?.trim();
  }

  openRoleShellFilter(): void {
    if (this.roleFilterModalOpen) {
      this.closeRoleShellFilterModal();
      return;
    }
    const names = this.adminUsersRef?.getShellRoleOptionNames() ?? [];
    const selectedRaw = this.adminUsersRef?.getShellSelectedRoleName() ?? '';
    const selectedSet = new Set(
      selectedRaw
        .split(',')
        .map((s) => s.trim())
        .filter(Boolean)
    );
    this.roleFilterModalConfig = {
      title: 'Role',
      placeholder: 'Search roles',
      options: names.map((name) => ({
        id: name,
        name,
        checked: selectedSet.has(name)
      })),
      showRecentlyAdded: false,
      showSearch: true
    };
    this.roleFilterModalOpen = true;
  }

  openStatusShellFilter(): void {
    if (this.statusFilterModalOpen) {
      this.closeStatusShellFilterModal();
      return;
    }
    const labels = this.adminUsersRef?.getShellStatusOptionLabels() ?? [];
    const selectedLabel = this.adminUsersRef?.getShellSelectedStatusLabel() ?? '';
    this.statusFilterModalConfig = {
      title: 'Status',
      placeholder: 'Search statuses',
      options: labels.map((name) => ({
        id: name,
        name,
        checked: selectedLabel === name
      })),
      showRecentlyAdded: false,
      showSearch: true
    };
    this.statusFilterModalOpen = true;
  }

  onRoleShellFilterApplied(selected: FilterOption[]): void {
    const value = selected.length ? selected.map((o) => String(o.name)).join(',') : '';
    this.adminUsersRef?.applyShellTextFilter({ filterKey: 'role', value });
    this.roleFilterModalOpen = false;
  }

  onStatusShellFilterApplied(selected: FilterOption[]): void {
    const value = selected.length ? selected.map((o) => String(o.name)).join(',') : '';
    this.adminUsersRef?.applyShellTextFilter({ filterKey: 'status', value });
    this.statusFilterModalOpen = false;
  }

  closeRoleShellFilterModal(): void {
    this.roleFilterModalOpen = false;
  }

  closeStatusShellFilterModal(): void {
    this.statusFilterModalOpen = false;
  }

  clearAllUsersFiltersFromShell(): void {
    this.adminUsersRef?.clearFiltersFromShell();
  }



  private checkForTabParameter(): void {
    // Check if we're on specific admin routes
    const currentUrl = this.router.url;
    if (currentUrl.includes('/admin/users')) {
      this.selectedTab.set('users');
      return;
    }
    
    if (currentUrl.includes('/admin/roles')) {
      this.selectedTab.set('roles');
      return;
    }

    this.route.queryParams.subscribe(params => {
      const tab = params['tab'];
      if (tab && ['users', 'teams', 'roles', 'subscription'].includes(tab)) {
        this.selectedTab.set(tab as 'users' | 'teams' | 'roles' | 'subscription');
      }
    });
  }

  // Method for datatable row click - will be used when datatable is ready
  navigateToRoleDetailsFromRow(roleId: number): void {
    // Get tenant ID from current route
    const tenantId = this.route.snapshot.paramMap.get('tenantId');
    if (tenantId) {
      this.router.navigate([`/${tenantId}/admin/roles/edit-role/${roleId}`], { queryParams: { mode: 'edit' } });
    } else {
      this.router.navigate(['/login']);
    }
  }

  private setAdminBreadcrumbs(): void {
    // Hide breadcrumbs on the main Admin page since it's a primary page
    // Secondary pages (edit user, role details, add user) will show their own breadcrumbs
    this.breadcrumbService.setBreadcrumbs([]);
  }

  private checkForActionResultParameter(): void {
    this.route.queryParams.subscribe(params => {
      const actionResult = params['actionResult'];
      if (actionResult) {
        this.handleActionResult(actionResult);
        // Clear the actionResult query parameter immediately after handling
        const currentParams = { ...params };
        delete currentParams['actionResult'];
        this.router.navigate([], {
          queryParams: currentParams,
          replaceUrl: true
        });
      }
    });
  }

  private handleActionResult(actionResult: string): void {
    switch (actionResult) {
      case 'role-deleted':
        this.actionFeedback.showSuccess('Role Deleted');
        break;
      case 'role-deleted-users-updated':
        this.actionFeedback.showSuccess('Role Deleted: Users Updated');
        break;
    }
  }
} 